/**
 * IFC Parser Service (web-ifc)
 *
 * Parses raw .ifc bytes in-process with the web-ifc wasm engine and flattens
 * every geometric product into a serializable record: baked mesh parts
 * (positions/normals/indices in metres, product-local frame) plus metadata
 * (name, IFC type, app category, property sets). Mirrors the lazy wasm
 * singleton pattern of CadParserService — the engine loads once per session,
 * the wasm binary is served from `public/ifc-wasm/`.
 */

// Type-only: the web-ifc value module (a large wasm loader) is imported
// dynamically inside getIfcApi so this file can ride in eagerly-loaded
// chunks (MCP bridge, dialogs) without pinning the library there.
import type * as WebIFC from 'web-ifc';

export interface IfcMeshPartRecord {
    /** Flat xyz per vertex. */
    positions: Float32Array;
    /** Flat xyz normal per vertex. */
    normals: Float32Array;
    /** Triangle index buffer into positions/normals. */
    indices: Uint32Array;
    /** RGB in [0,1] from the placed style colour. */
    color: [number, number, number];
}

export interface IfcPropertyRecord {
    set: string; // Pset name (e.g. "Pset_DoorCommon")
    name: string; // Property name
    value: string; // Display value (unit suffix preserved as authored)
}

export interface IfcProductRecord {
    expressID: number;
    name: string;
    ifcType: string; // e.g. "IfcDoor"
    category: string; // App category ("Doors", "Furniture", ...)
    parts: IfcMeshPartRecord[];
    properties: IfcPropertyRecord[];
    /** Axis-aligned bbox in the product-local frame, metres. */
    bbox: { min: [number, number, number]; max: [number, number, number] };
}

export interface IfcParseResult {
    sourceName: string;
    schema: string;
    unitScale: number; // metres per IFC length unit
    products: IfcProductRecord[];
}

let ifcApiPromise: Promise<WebIFC.IfcAPI> | null = null;
// The wasm loader module itself: resolved once on first use (see getIfcApi).
let webIfcModule: typeof import('web-ifc') | null = null;

async function loadWebIfcModule(): Promise<typeof import('web-ifc')> {
    if (!webIfcModule) {
        webIfcModule = await import('web-ifc');
    }
    return webIfcModule;
}

/** Sync access to the wasm loader module after getIfcApi() resolved it. */
function requireWebIfcModule(): typeof import('web-ifc') {
    if (!webIfcModule) {
        throw new Error('web-ifc module accessed before getIfcApi() loaded it.');
    }
    return webIfcModule;
}

/** Lazy singleton: boots the web-ifc wasm module once per session. */
export async function getIfcApi(): Promise<WebIFC.IfcAPI> {
    if (!ifcApiPromise) {
        ifcApiPromise = (async () => {
            const WebIFCVals = await loadWebIfcModule();
            const api = new WebIFCVals.IfcAPI();
            await api.Init(
                (path: string) => `${import.meta.env.BASE_URL}ifc-wasm/${path}`
            );
            return api;
        })().catch((err) => {
            ifcApiPromise = null;
            throw err;
        });
    }
    return ifcApiPromise;
}

// ==================== Type → category mapping ====================

/**
 * IFC entity name → the app's BIM-style category. Values must match the
 * categories handled by BIMElementRenderer so selection/visibility behave.
 */
const IFC_TYPE_CATEGORY: Record<string, string> = {
    IfcDoor: 'Doors',
    IfcWindow: 'Windows',
    IfcCurtainWall: 'Curtain Walls',
    IfcWall: 'Walls',
    IfcWallStandardCase: 'Walls',
    IfcSlab: 'Floors',
    IfcRoof: 'Roofs',
    IfcBeam: 'Structural Framing',
    IfcColumn: 'Structural Columns',
    IfcFooting: 'Structural Foundations',
    IfcMember: 'Structural Framing',
    IfcPlate: 'Structural Framing',
    IfcStair: 'Stairs',
    IfcRamp: 'Ramps',
    IfcRailing: 'Railings',
    IfcFurniture: 'Furniture',
    IfcFurnishingElement: 'Furniture',
    IfcSystemFurnitureElement: 'Furniture',
    IfcLightFixture: 'Lighting Fixtures',
    IfcFlowTerminal: 'Mechanical Equipment',
    IfcFlowFitting: 'Mechanical Equipment',
    IfcFlowMovingDevice: 'Mechanical Equipment',
    IfcFlowStorageDevice: 'Mechanical Equipment',
    IfcFlowTreatmentDevice: 'Mechanical Equipment',
    IfcDistributionControlElement: 'Electrical Equipment',
    IfcDistributionFlowElement: 'Mechanical Equipment',
    IfcPipeSegment: 'Pipes',
    IfcDuctSegment: 'Ducts',
    IfcCovering: 'Ceilings',
    IfcSign: 'Generic Models',
    IfcBuildingElementProxy: 'Generic Models',
    IfcOpeningElement: 'Structural Openings',
};

export function ifcTypeToCategory(ifcTypeName: string): string {
    // Lookup is case-insensitive: web-ifc may report "IFCDOOR" or "IfcDoor"
    // depending on the line-flattening path.
    const needle = ifcTypeName.toUpperCase();
    for (const [key, category] of Object.entries(IFC_TYPE_CATEGORY)) {
        if (key.toUpperCase() === needle) return category;
    }
    return 'Generic Models';
}

/** Normalise web-ifc `__type` ("IFCDOOR" / "IfcDoor") to schema spelling. */
export function normalizeIfcType(raw: string): string {
    if (!raw) return 'IfcElement';
    const upper = raw.toUpperCase();
    return 'Ifc' + upper.charAt(3) + upper.slice(4).toLowerCase();
}

// ==================== Unit resolution ====================

/** IFC length-unit enumerations → metres. Everything else stays 1 (metric guess). */
const LENGTH_UNIT_TO_METRES: Record<string, number> = {
    METRE: 1,
    MILLIMETRE: 0.001,
    CENTIMETRE: 0.01,
    DECIMETRE: 0.1,
    MICROMETRE: 1e-6,
    KILOMETRE: 1000,
};

function resolveUnitScale(api: WebIFC.IfcAPI, modelID: number): number {
    let expressID = -1;
    let scanned = 0;
    // Linear scan over the header-limited id space looking for the SI length
    // unit; capped so a pathological file cannot hang the main thread.
    while (scanned++ < 500_000) {
        expressID = api.GetNextExpressID(modelID, expressID);
        if (expressID === -1) break;
        const type = api.GetLineType(modelID, expressID);
        // Safe: resolveUnitScale only runs after getIfcApi() loaded the module.
        if (type !== requireWebIfcModule().IFCSIUNIT) continue;
        try {
            const line = api.GetLine(modelID, expressID) as any;
            const unitType = String(line?.UnitType?.value ?? '');
            const name = String(line?.Name?.value ?? '');
            if (unitType === '.LENGTHUNIT.' && name in LENGTH_UNIT_TO_METRES) {
                return LENGTH_UNIT_TO_METRES[name];
            }
        } catch {
            // Malformed unit line: ignore and keep scanning.
        }
    }
    return 1; // METRE default
}

// ==================== Geometry extraction ====================

/**
 * Build one flat mesh part from a placed geometry, baking its 4x4 placement
 * matrix into the vertex positions so the final asset lives in a single
 * product-local frame (identity transform at placement time).
 */
function extractPart(
    api: WebIFC.IfcAPI,
    modelID: number,
    geometryExpressID: number,
    flatTransformation: number[],
    color: WebIFC.Color,
    scale: number
): IfcMeshPartRecord | null {
    const geometry = api.GetGeometry(modelID, geometryExpressID);
    const vertexPtr = geometry.GetVertexData();
    const vertexSize = geometry.GetVertexDataSize();
    const indexPtr = geometry.GetIndexData();
    const indexSize = geometry.GetIndexDataSize();

    // web-ifc vertex layout: x,y,z,nx,ny,nz interleaved (6 floats/vertex).
    const rawVerts = api.GetVertexArray(vertexPtr, vertexSize);
    const rawIndices = api.GetIndexArray(indexPtr, indexSize);
    geometry.delete();
    if (!rawVerts || rawVerts.length === 0) return null;

    const vertexCount = rawVerts.length / 6;
    const positions = new Float32Array(vertexCount * 3);
    const normals = new Float32Array(vertexCount * 3);

    // Column-major 4x4 placement; positions are first converted to metres,
    // then baked through the matrix (translation may already carry scaling
    // for nested placements — the matrix dominates, the unit only normalises
    // the raw wasm vertex data).
    const m = flatTransformation;
    const unit = scale;

    for (let i = 0; i < vertexCount; i++) {
        const x = rawVerts[i * 6] * unit;
        const y = rawVerts[i * 6 + 1] * unit;
        const z = rawVerts[i * 6 + 2] * unit;
        positions[i * 3] = m[0] * x + m[4] * y + m[8] * z + m[12];
        positions[i * 3 + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
        positions[i * 3 + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];
        // Normal: rotation part only (upper 3x3), no translation.
        const nx = rawVerts[i * 6 + 3];
        const ny = rawVerts[i * 6 + 4];
        const nz = rawVerts[i * 6 + 5];
        normals[i * 3] = m[0] * nx + m[4] * ny + m[8] * nz;
        normals[i * 3 + 1] = m[1] * nx + m[5] * ny + m[9] * nz;
        normals[i * 3 + 2] = m[2] * nx + m[6] * ny + m[10] * nz;
    }

    // Copy out of the wasm heap before the model closes.
    const indices = new Uint32Array(rawIndices);
    return {
        positions,
        normals,
        indices,
        color: [color.x, color.y, color.z],
    };
}

// ==================== Property extraction ====================

function flattenProps(api: WebIFC.IfcAPI, modelID: number, expressID: number): IfcPropertyRecord[] {
    const out: IfcPropertyRecord[] = [];
    try {
        // Psets attached to the element (instance level is enough for a
        // static library entry; type properties come via the type object).
        const psets = api.GetLine(modelID, expressID, true, true, 'IsDefinedBy') as any;
        const rels = Array.isArray(psets) ? psets : [];
        for (const rel of rels) {
            const def = rel?.RelatingPropertyDefinition;
            if (!def || def.type !== 'IFCPROPERTYSET') continue;
            const setName = String(def.Name?.value ?? 'Properties');
            const props: any[] = def.HasProperties ?? [];
            for (const p of props) {
                const name = String(p?.Name?.value ?? '');
                if (!name) continue;
                const raw = p?.NominalValue?.value;
                out.push({ set: setName, name, value: raw === undefined || raw === null ? '' : String(raw) });
            }
        }
    } catch {
        // Property navigation failures must never block geometry import.
    }
    return out;
}

// ==================== Public entry ====================

/**
 * Tessellate one streamed product into a flat record: bake parts into the
 * product-local frame (placement matrix + unit scale), re-centre to the
 * family origin, collect metadata. Called from StreamAllMeshes; the caller
 * owns deleting the flatMesh.
 */
function collectProduct(
    api: WebIFC.IfcAPI,
    modelID: number,
    flatMesh: WebIFC.FlatMesh,
    unitScale: number,
    products: IfcProductRecord[],
    recentre = true
): void {
    const expressID = flatMesh.expressID;
    let line: any = null;
    try {
        line = api.GetLine(modelID, expressID);
    } catch {
        return;
    }
    const ifcTypeName = line?.__type ? normalizeIfcType(String(line.__type)) : 'IfcElement';
    const parts: IfcMeshPartRecord[] = [];
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

    for (let g = 0; g < flatMesh.geometries.size(); g++) {
        const placed = flatMesh.geometries.get(g);
        const part = extractPart(
            api, modelID,
            placed.geometryExpressID,
            placed.flatTransformation as unknown as number[],
            placed.color,
            unitScale
        );
        if (!part || part.positions.length === 0) continue;
        parts.push(part);
        for (let i = 0; i < part.positions.length; i += 3) {
            if (part.positions[i] < minX) minX = part.positions[i];
            if (part.positions[i] > maxX) maxX = part.positions[i];
            if (part.positions[i + 1] < minY) minY = part.positions[i + 1];
            if (part.positions[i + 1] > maxY) maxY = part.positions[i + 1];
            if (part.positions[i + 2] < minZ) minZ = part.positions[i + 2];
            if (part.positions[i + 2] > maxZ) maxZ = part.positions[i + 2];
        }
    }
    if (parts.length === 0) return;

    // Re-centre the baked geometry so the product sits on its family
    // origin: XY at bbox center, Z at bbox min (stands on the floor),
    // mirroring how ComponentTool places elements at their position.
    // Link flows keep world coordinates instead (recentre=false) so every
    // product of a whole-model link lands at its authored site position.
    const cx = recentre ? (minX + maxX) / 2 : 0;
    const cy = recentre ? (minY + maxY) / 2 : 0;
    const cz = recentre ? minZ : 0;
    for (const part of parts) {
        for (let i = 0; i < part.positions.length; i += 3) {
            part.positions[i] -= cx;
            part.positions[i + 1] -= cy;
            part.positions[i + 2] -= cz;
        }
    }

    const name = String(line?.Name?.value ?? '').trim() || `${ifcTypeName} #${expressID}`;
    products.push({
        expressID,
        name,
        ifcType: ifcTypeName,
        category: ifcTypeToCategory(ifcTypeName),
        parts,
        properties: flattenProps(api, modelID, expressID),
        bbox: {
            min: [minX - cx, minY - cy, minZ - cz],
            max: [maxX - cx, maxY - cy, maxZ - cz],
        },
    });
}

/**
 * Parse IFC bytes into flat product records ready for the family registry.
 * Runs synchronously on the wasm engine between await points; large models
 * take a moment but keep the API surface identical to parseCadFile.
 * `options.recentre=false` keeps every product at its authored world
 * position (whole-model link flows); the default re-centres per product
 * for the family placement flow.
 */
export async function parseIfcFile(
    fileName: string,
    bytes: Uint8Array,
    options: { recentre?: boolean } = {}
): Promise<IfcParseResult> {
    const api = await getIfcApi();
    const modelID = api.OpenModel(bytes, { COORDINATE_TO_ORIGIN: false });
    if (modelID === -1 || modelID === undefined) {
        throw new Error(`web-ifc could not open "${fileName}" — unsupported or corrupt IFC file.`);
    }
    try {
        const schema = api.GetModelSchema(modelID);
        const unitScale = resolveUnitScale(api, modelID);

        const products: IfcProductRecord[] = [];
        api.StreamAllMeshes(modelID, (flatMesh) => {
            try {
                collectProduct(api, modelID, flatMesh, unitScale, products, options.recentre !== false);
            } finally {
                flatMesh.delete();
            }
        });

        return {
            sourceName: fileName,
            schema,
            unitScale,
            products,
        };
    } finally {
        api.CloseModel(modelID);
    }
}
